// src/components/ExportToolbar.tsx
//
// ★★★ WAVE 27 — ONE control group for the four things you can do with a report.
//
// ══ ⚠ WHAT WAS WRONG, from the owner's own screen ══
//
// Print sat alone in the top-right corner. Download PDF, Download Excel and Email
// sat in a separate row under the description, spread across its full width with
// uneven gaps. Four controls, four identical outline buttons, no grouping, no
// icons, and the one that leaves the building looking exactly like the one that
// opens a print dialog.
//
// That is three separate problems and they compound:
//
//   1. **Proximity.** Gestalt's first rule and the cheapest one to get right:
//      things that belong together are placed together. Split across two rows with
//      a paragraph between them, Print reads as a page action and the other three
//      read as something else — when all four are the same question, *"what do I
//      want to do with this list?"*
//   2. **No differentiation.** Four `variant="outline"` buttons of near-identical
//      width are four identical grey rectangles. A reader scanning for "Excel"
//      reads all four labels. An icon per action turns that into one glance.
//      ★ UPDATED 2026-10-14 (owner direction, ux-direction.md §4.4): Print, Email and Refresh are now an
//      icon ALONE (named by hidden text and a tooltip), because their glyphs are universal; the two
//      Downloads keep icon AND words, for the C-16 reason below.
//   3. **No hierarchy.** Nothing said which action is ordinary and which is
//      consequential. Emailing a report sends a document containing scope-filtered
//      operational data OUT of the building; printing it does not. They were
//      indistinguishable.
//
// ══ THE FIX, and what it deliberately does NOT do ══
//
// One right-aligned cluster, in the order the actions escalate: Print (stays on
// this machine), Download PDF, Download Excel (leave as files), Email (leaves the
// building). Icons beside labels. A hairline divider before Email, so the one with
// a different consequence is visibly its own thing.
//
// ⚠ **THE LABELS KEEP THE WORD "DOWNLOAD", and shortening them was tried and
// reverted.** "PDF" beside a document icon is tidier and says only what the FORMAT
// is; "Download PDF" says what the button DOES. C-16 (P2C1 §12.52) is a
// contractual requirement that every query be downloadable in both formats, and an
// evaluator reading this toolbar should find the requirement in the words rather
// than infer it from an icon. Twelve tests assert those labels, and they are
// asserting the right thing.
//
// ⚠ **NO PRIMARY BUTTON HERE.** A filled button is a page's ONE recommended
// action, and none of these is: an officer arrives at a list to read it, not to
// export it. Promoting Download PDF to primary — the obvious "make it look nicer"
// move — would put the loudest control on the page next to a thing nobody came to
// do, and `ux-direction.md` §4's own rule is one primary action per surface.
//
// ⚠ **NOT A DROPDOWN.** Collapsing four actions behind an "Export ▾" menu is
// tidier and worse: C-16 (P2C1 §12.52) is a contractual requirement that every
// query be printable and downloadable in both formats, and an evaluator checking
// it should SEE it, not discover it behind a menu. Visible beats tidy when the
// visibility is the requirement.
//
// ══ ★★★ A CONTROL THAT LOOKS LIVE MUST NEVER DO NOTHING ══
//
// **Reported from the owner's own screen:** on the custom report, *Download PDF*
// did nothing at all until *Show preview* had been pressed. The button was fully
// enabled, took the click, and the caller's handler was
// `if (appliedParams !== null) window.location.href = …` — so a press with no
// preview fell off the end of the guard and produced **no file, no message and no
// change of any kind**. The screen's own explanatory sentence was already on the
// page and the owner still read it as a defect, which is the whole lesson: a
// paragraph nearby does not cancel a control that swallows a click.
//
// ⚠ **AND DISABLING THE BUTTON WOULD NOT HAVE FIXED IT.** A disabled button fires
// no click event, so a user who presses it gets exactly the same nothing, minus
// even the cursor feedback — the identical "is this broken?" moment, which is what
// was reported. The fix has to make the press *say something*.
//
// So `downloadBlockedReason` keeps the buttons **live** and turns the press into
// an answer: the reason is announced in a `role="alert"` region the moment it is
// pressed, and it is attached to the buttons by `aria-describedby` so a screen
// reader hears the precondition BEFORE the press as well as after it. The reason
// text is the caller's, because only the caller knows the way out — and it must
// name that way out, not merely state the rule (`ENGINEERING.md`: *when you ship a
// refusal, ship the way out in the same change*).
//
// ★ **The general rule, for every surface in this product:** a control that is
// visible and enabled must, on every press, either do the thing or say why it
// cannot. Silence is the one outcome that is never acceptable, because the user
// cannot tell it apart from a fault.
import { useState, type ReactElement, type ReactNode } from 'react'
import { SgdsButton } from '@govtechsg/sgds-web-component/react'
import { Glyph } from './icons'
import { IconButton } from './IconButton'

export interface ExportToolbarProps {
  /** Fired for the print action. Omit to leave Print out entirely. */
  readonly onPrint?: () => void
  /** Called with the format the officer chose. Omit to leave the downloads out. */
  readonly onDownload?: (format: 'pdf' | 'xlsx') => void
  /**
   * ★★★ Why a download cannot run YET — and it must name the way out.
   *
   * Set this whenever `onDownload` would refuse or no-op. The buttons stay
   * enabled and pressing one announces this sentence instead of doing nothing;
   * see this file's header for why disabling them would reproduce the very
   * defect this exists to fix.
   *
   * ⚠ **A rule, not a preference: it must say what to do next.** "Preview the
   * report first" is a way out. "No report is available" is a dead end wearing a
   * message, and leaves the reader exactly as stuck as silence did.
   *
   * Leave unset when the downloads can always run — which is every caller but
   * the custom report today, and the reason none of them needed this.
   */
  readonly downloadBlockedReason?: string
  /**
   * The Email control, supplied by the caller rather than built here.
   *
   * ⚠ `EmailReportButton` owns a panel, a request and the sentence the server
   * returns about whether anything will actually be sent. This component owns
   * ARRANGEMENT. Rebuilding the email flow inside a toolbar would put that
   * honesty notice in two places, and one of them would drift.
   */
  readonly email?: ReactNode
  /**
   * Reload the list. Rendered as an icon after Email, beside the other quick actions, so a screen's Refresh sits with
   * them instead of as a stray outline button of its own. Omit to leave it out.
   */
  readonly onRefresh?: () => void
}

export function ExportToolbar({ onPrint, onDownload, email, onRefresh, downloadBlockedReason }: ExportToolbarProps): ReactElement {
  /*
   * ⚠ Whether the officer has actually PRESSED a blocked download, not whether
   * one is blocked. The reason is attached to the buttons by `aria-describedby`
   * from the start, so assistive technology has it before the press; rendering
   * the visible alert before anyone has tried anything would put a standing
   * warning under a toolbar on a screen where nothing is yet wrong.
   */
  const [refused, setRefused] = useState(false)
  const blocked = downloadBlockedReason !== undefined && downloadBlockedReason.length > 0
  const reasonId = 'pt-export-blocked-reason'

  const download = (format: 'pdf' | 'xlsx'): void => {
    if (blocked) {
      // ★ The press produces an ANSWER. This is the line the defect was missing.
      setRefused(true)
      return
    }
    setRefused(false)
    onDownload?.(format)
  }

  return (
    <div className="pt-export-toolbar pt-no-print" role="group" aria-label="Print, download or email this report">
      {onPrint !== undefined && (
        <IconButton glyph="print" label="Print" onClick={onPrint} />
      )}
      {onDownload !== undefined && (
        <>
          <SgdsButton
            variant="outline"
            type="button"
            onClick={() => download('pdf')}
            {...(blocked ? { 'aria-describedby': reasonId } : {})}
          >
            <span className="pt-export-action">
              <Glyph name="pdf" />
              Download PDF
            </span>
          </SgdsButton>
          <SgdsButton
            variant="outline"
            type="button"
            onClick={() => download('xlsx')}
            {...(blocked ? { 'aria-describedby': reasonId } : {})}
          >
            <span className="pt-export-action">
              <Glyph name="excel" />
              Download Excel
            </span>
          </SgdsButton>
        </>
      )}
      {email !== undefined && (
        <>
          {/*
            ⚠ THE DIVIDER IS THE POINT, not decoration. Email is the only one of
            these that sends operational data OUT of the building; the other three
            end on this machine. A reader should be able to see that difference
            before they press it, and a separator is the quietest way to say it.
          */}
          <span className="pt-export-divider" aria-hidden="true" />
          {email}
        </>
      )}
      {onRefresh !== undefined && <IconButton glyph="refresh" label="Refresh" onClick={onRefresh} />}
      {/*
        ⚠ ALWAYS RENDERED WHEN BLOCKED, so `aria-describedby` above always resolves
        to a real element — a description pointing at an id that does not exist is
        announced as nothing at all. It is visually hidden until a press happens,
        and `role="alert"` then announces it.
      */}
      {blocked && (
        <p
          id={reasonId}
          role="alert"
          className={
            refused
              ? 'pt-export-blocked text-sm m-0 text-[var(--sgds-danger-color-emphasis)]'
              : 'sr-only'
          }
        >
          {downloadBlockedReason}
        </p>
      )}
    </div>
  )
}
